import { CreditCard, Scroll, Bell } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type CustomerPaymentSetup = 'valid' | 'expiring_soon' | 'expired' | 'none';

export interface CustomerSetupIconsProps {
  /** Payment method status (matches the value indexed for search + computed in the details panel). */
  payment: CustomerPaymentSetup;
  /** Whether the customer has accepted terms & conditions. */
  termsAccepted: boolean;
  /** Whether the customer has at least one primary contact. */
  hasPrimaryContact: boolean;
  /** When provided, the payment icon becomes a button (used in the details panel to open the payment tab). */
  onPaymentClick?: () => void;
  className?: string;
  iconClassName?: string;
}

const PAYMENT_TOOLTIP: Record<CustomerPaymentSetup, string> = {
  valid: 'Payment method active',
  expiring_soon: 'Card expiring soon',
  expired: 'Card expired — needs updating',
  none: 'No payment method on file',
};

/**
 * The three customer setup indicators — payment method, terms acceptance, and
 * primary contact — shown in the map details panel header and on customer
 * search result cards (RUT-1494). Single source of truth so the two surfaces
 * always match in appearance and meaning.
 */
export function CustomerSetupIcons({
  payment,
  termsAccepted,
  hasPrimaryContact,
  onPaymentClick,
  className,
  iconClassName = 'h-4 w-4',
}: CustomerSetupIconsProps) {
  const paymentIcon = (
    <CreditCard
      className={cn(iconClassName, {
        'text-green-500': payment === 'valid',
        'text-orange-500': payment === 'expiring_soon',
        'text-red-500': payment === 'expired' || payment === 'none',
      })}
      weight="regular"
    />
  );

  return (
    <TooltipProvider delayDuration={0}>
      <div className={cn('flex items-center gap-2', className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            {onPaymentClick ? (
              <button type="button" onClick={onPaymentClick} className="cursor-pointer" aria-label="Open payment tab">
                {paymentIcon}
              </button>
            ) : (
              <span className="cursor-help">{paymentIcon}</span>
            )}
          </TooltipTrigger>
          <TooltipContent side="bottom">{PAYMENT_TOOLTIP[payment]}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help">
              <Scroll className={cn(iconClassName, termsAccepted ? 'text-green-500' : 'text-red-500')} weight="regular" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {termsAccepted ? 'Terms & conditions accepted' : 'Terms & conditions not accepted'}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help">
              <Bell className={cn(iconClassName, hasPrimaryContact ? 'text-green-500' : 'text-red-500')} weight="regular" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {hasPrimaryContact ? 'Primary contact set' : 'No primary contact'}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
